import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoanSimulation } from '../../contexts/LoanSimulationContext';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import Options from '../../components/Options/Options';
import { CounterOfferData, OptionsEnum } from '../../interfaces/CounterOffer';
import { TextBold, TextMedium } from '../../styles/GlobalStyledComponents';
import {
  ConditionsSection,
  ConditionsWrapper,
  Container,
  CounterOfferButton,
  CounterOfferButtonSection,
  OptionsList,
  OptionsWrapper,
  RequestAmount,
  SummaryBox,
  SummaryDetails,
  SummaryDetailsInfo,
  SummaryElement,
  SummaryRow,
  SummaryWrapper,
  SeparationLine,
  SubtitleTextbold,
  SummarySection,
  Title,
  TodayHighlight,
  TodayHighlightText,
} from './CounterOfferStyledComponents';
import { pricesHelper } from '../../utils/paymentsHelper';
import CreditProposalService from '../../services/CreditProposal';
import { ROUTES_PATH } from '../../constants';
import {
  finalDate,
  formatDate,
  formatNumberToDate,
} from '../../utils/date-helper';
import InsightService from '../../services/Insight';
import LoanSimulationService from '../../services/LoanSimulation';
import { CommercialOfferType } from '../../services/LoanSimulation/types';
import { LoanSimulation } from '../../interfaces/LoanSimulation';

interface CounterOfferProps {
  loadingEvent: (data: boolean) => void;
}

export const CounterOffer: React.FC<CounterOfferProps> = ({ loadingEvent }) => {
  let navigate = useNavigate();

  const { creditProposalResult, createCreditProposalResult } =
    useCreditProposalResult();
  const { loanSimulation, createLoanSimulation } = useLoanSimulation();

  const [tirSelected, setTirSelected] = useState<boolean>(false);
  const [termReduceSelected, setTermReduceSelected] = useState<boolean>(false);
  const [amountIncreaseSelected, setAmountIncreaseSelected] =
    useState<boolean>(false);
  const [installmentReduceSelected, setInstallmentReduceSelected] =
    useState<boolean>(false);
  const [enableTirOption, setEnableTirOption] = useState<boolean>(false);
  const [enableTermOption, setEnableTermOption] = useState<boolean>(false);
  const [enableAmountOption, setEnableAmountOption] = useState<boolean>(false);
  const [enableInstallmentOption, setEnableInstallmentOption] =
    useState<boolean>(false);
  const [tirOptionData, setTirOptionData] = useState<CounterOfferData | null>(
    null,
  );
  const [termOptionData, setTermOptionData] = useState<CounterOfferData | null>(
    null,
  );
  const [amountOptionData, setAmountOptionData] =
    useState<CounterOfferData | null>(null);
  const [installmentOptionData, setInstallmentOptionData] =
    useState<CounterOfferData | null>(null);
  const [initValue, setInitValue] = useState<boolean>(true);
  const [counterOfferSelected, setCounterOfferSelected] =
    useState<CounterOfferData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const counterOfferData = creditProposalResult?.counter_offers || [];

  const requestedAmount = creditProposalResult?.requested_amount;
  const terms = creditProposalResult?.term || loanSimulation?.term;
  const regularFee = loanSimulation?.regular_fee;
  const openingFeeAmount =
    Number(counterOfferSelected?.financial.opening_fee_amount) || 0;

  // Pueden ser TIR, TERM, AMOUNT o INSTALLMENT.
  const saveOptionsInfo = () => {
    if (counterOfferData && counterOfferData.length > 0) {
      const data = counterOfferData;
      const MAX_OPTIONS = 2;
      let enabledCount = 0;

      const sortInstallments = (option: CounterOfferData) => {
        // No usar toSorted (ES2023): rompe en navegadores antiguos (Safari < 16).
        option.financial.installments = [
          ...(option.financial.installments ?? []),
        ].sort((a, b) => a.due_number - b.due_number);
      };

      // TIR (máxima prioridad)
      if (enabledCount < MAX_OPTIONS) {
        const tirOption = data.find(
          (option) => option.name === OptionsEnum.TIR,
        );
        if (tirOption) {
          sortInstallments(tirOption);
          setEnableTirOption(true);
          setTirOptionData(tirOption);
          enabledCount++;
        }
      }
      // TERM
      if (enabledCount < MAX_OPTIONS) {
        const termOption = data.find(
          (option) => option.name === OptionsEnum.TERM,
        );
        if (termOption) {
          sortInstallments(termOption);
          setEnableTermOption(true);
          setTermOptionData(termOption);
          enabledCount++;
        }
      }
      // INSTALLMENT
      if (enabledCount < MAX_OPTIONS) {
        const installmentOption = data.find(
          (option) => option.name === OptionsEnum.INSTALLMENT,
        );
        if (installmentOption) {
          sortInstallments(installmentOption);
          setEnableInstallmentOption(true);
          setInstallmentOptionData(installmentOption);
          enabledCount++;
        }
      }
      // AMOUNT
      if (enabledCount < MAX_OPTIONS) {
        const amountOption = data.find(
          (option) => option.name === OptionsEnum.AMOUNT,
        );
        if (amountOption) {
          sortInstallments(amountOption);
          setEnableAmountOption(true);
          setAmountOptionData(amountOption);
          enabledCount++;
        }
      }
      loadingEvent(false);
    }
  };

  // Se invoca la simulación original al retomar/continuar ya que se pierde la cuota en el proceso
  const getLoanSimulation = async () => {
    const simulationBody = creditProposalResult;
    if (!simulationBody) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
      return;
    }
    const sim = {
      amount: simulationBody.requested_amount,
      flows: ['CEX', 'PLS', 'SUS', 'ECO'],
      term: simulationBody.term,
    };
    const commercialOffers = await LoanSimulationService.create(sim);
    const offerSelected = commercialOffers.commercial_offers.find(
      (e: CommercialOfferType) =>
        e.commercial_offer_id === simulationBody.commercial_offer_id,
    );
    const simulations = offerSelected?.financial_offers;
    if (simulations) {
      const response = simulations.find((e: LoanSimulation) => {
        return e.term === simulationBody.term;
      });
      if (response && simulationBody.commercial_offer_id) {
        response.commercial_offer_id = simulationBody.commercial_offer_id;
        response.flow = offerSelected.flow;
        createLoanSimulation(response);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } else {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const handleTirClick = () => {
    setTirSelected(true);
    setCounterOfferSelected(tirOptionData);
    // Ajuste de las opciones
    setTermReduceSelected(false);
    setAmountIncreaseSelected(false);
    setInstallmentReduceSelected(false);
    setInitValue(false);
  };

  const handleTermReduceClick = () => {
    setTermReduceSelected(true);
    setCounterOfferSelected(termOptionData);
    // Ajuste de las opciones
    setTirSelected(false);
    setAmountIncreaseSelected(false);
    setInstallmentReduceSelected(false);
    setInitValue(false);
  };

  const handleAmountIncreaseClick = () => {
    setAmountIncreaseSelected(true);
    setCounterOfferSelected(amountOptionData);
    // Ajuste de las opciones
    setTirSelected(false);
    setTermReduceSelected(false);
    setInstallmentReduceSelected(false);
    setInitValue(false);
    setCounterOfferSelected(amountOptionData);
  };

  const handleInstallmentReduceClick = () => {
    setInstallmentReduceSelected(true);
    setCounterOfferSelected(installmentOptionData);
    // Ajuste de las opciones
    setTirSelected(false);
    setAmountIncreaseSelected(false);
    setTermReduceSelected(false);
    setInitValue(false);
  };

  const handleOptionClick = async () => {
    const counterOfferId = counterOfferSelected?.id;

    // Aquí se lanzaría el endpoint de aceptación de contraoferta con el ID correspondiente
    try {
      if (creditProposalResult && counterOfferId) {
        loadingEvent(true);
        setLoading(true);
        const response = await CreditProposalService.AssignCounterOffer(
          creditProposalResult.credit_proposal_id,
          counterOfferId,
        );
        if (response) {
          // Se ajusta el flujo al step que toque y se manda a la pantalla de operation shipping para seleccionar dispositivo
          createCreditProposalResult({
            ...creditProposalResult,
            flow_step: response.flow_step,
            counter_offer_id: counterOfferId,
          });
          navigate(ROUTES_PATH.OPERATION_SHIPPING);
          loadingEvent(false);
          setLoading(false);
          InsightService.logTrace('COUNTEROFFER', [{ counteroffer: response }]);
        }
      }
    } catch (error: any) {
      console.error('Error al aceptar la contraoferta: ', error);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
      loadingEvent(false);
      setLoading(false);
    }
  };

  const handleNoContinue = async () => {
    navigate(`${ROUTES_PATH.QUIT}?isCounterOffer=true`);
  };

  const lastDueDate = (): string => {
    if (termOptionData && termOptionData.financial.installments.length > 1) {
      const installment = termOptionData.financial.installments.find(
        (inst) =>
          inst.due_number === termOptionData.financial.installments.length,
      );
      if (
        installment.due_date &&
        typeof installment.due_date === 'number' &&
        installment.fee &&
        Number(installment.fee) > 0
      ) {
        const newDate = formatNumberToDate(installment.due_date);
        return newDate;
      }
    } else return finalDate(terms - counterOfferSelected?.term || 0);
  };

  useEffect(() => {
    const init = async () => {
      loadingEvent(true);
      // Al retomar/continuar se pierde la cuota, así que recuperamos la simulación original.
      if (loanSimulation === null) {
        await getLoanSimulation();
      }
      saveOptionsInfo();
    };
    init();
  }, []);

  return (
    <Container id="counter-offer-container" $loading={loading}>
      <Title id="counter-offer-title">
        Uppss... necesitamos modificar el planteamiento de la financiación.
        Selecciona una de las siguientes alternativas
      </Title>
      <ConditionsWrapper id="conditions-wrapper">
        <SubtitleTextbold $fontSize={16}>
          Condiciones solicitadas
        </SubtitleTextbold>
        <ConditionsSection id="conditions-section">
          <RequestAmount $fontSize={12}>
            Importe solicitado:{' '}
            <TextBold $fontSize={12}>
              {requestedAmount && pricesHelper(requestedAmount)} €
            </TextBold>
          </RequestAmount>
          <TextMedium $fontSize={12}>
            Plazo: <TextBold $fontSize={12}>{terms} meses</TextBold>
          </TextMedium>
          <TextMedium $fontSize={12}>
            Cuota:{' '}
            <TextBold $fontSize={12}>
              {regularFee && pricesHelper(regularFee)} €
            </TextBold>
          </TextMedium>
        </ConditionsSection>
      </ConditionsWrapper>
      <OptionsWrapper id="options-wrapper">
        <SubtitleTextbold $fontSize={16}>Opciones aprobadas</SubtitleTextbold>
        <OptionsList id="options-list">
          {enableTirOption && (
            <Options
              type={OptionsEnum.TIR}
              isSelected={tirSelected}
              recommended={initValue}
              onClick={handleTirClick}
              installmentAmount={
                tirOptionData?.financial.installments.length > 1 &&
                tirOptionData.financial.installments[1].due_number === 2
                  ? pricesHelper(
                      Number(tirOptionData.financial.installments[1].fee),
                    )
                  : ''
              }
            />
          )}
          {enableTermOption && (
            <Options
              type={OptionsEnum.TERM}
              isSelected={termReduceSelected}
              recommended={initValue && !enableTirOption}
              onClick={handleTermReduceClick}
              dueDate={lastDueDate()}
              term={terms - termOptionData?.term || 0}
              installmentAmount={
                termOptionData?.financial.installments.length > 1 &&
                termOptionData.financial.installments[1].due_number === 2
                  ? pricesHelper(
                      Number(termOptionData.financial.installments[1].fee),
                    )
                  : ''
              }
            />
          )}
          {enableInstallmentOption && (
            <Options
              type={OptionsEnum.INSTALLMENT}
              isSelected={installmentReduceSelected}
              onClick={handleInstallmentReduceClick}
              recommended={initValue && !enableTirOption && !enableTermOption}
              firstFeeAmount={
                installmentOptionData?.financial.installments.length > 0 &&
                installmentOptionData.financial.installments[0].due_number === 1
                  ? pricesHelper(
                      Number(
                        installmentOptionData.financial.installments[0].fee,
                      ),
                    )
                  : ''
              }
              installmentAmount={
                installmentOptionData?.financial.installments.length > 1 &&
                installmentOptionData.financial.installments[1].due_number === 2
                  ? pricesHelper(
                      Number(
                        installmentOptionData.financial.installments[1].fee,
                      ),
                    )
                  : ''
              }
            />
          )}
          {enableAmountOption && (
            <Options
              type={OptionsEnum.AMOUNT}
              isSelected={amountIncreaseSelected}
              recommended={
                initValue &&
                !enableTirOption &&
                !enableTermOption &&
                !enableInstallmentOption
              }
              onClick={handleAmountIncreaseClick}
              requestAmount={
                amountOptionData?.requested_amount &&
                pricesHelper(amountOptionData.requested_amount)
              }
              installmentAmount={
                amountOptionData?.financial.installments.length > 1 &&
                amountOptionData.financial.installments[1].due_number === 2
                  ? pricesHelper(amountOptionData.financial.installments[1].fee)
                  : ''
              }
            />
          )}
        </OptionsList>
      </OptionsWrapper>

      {counterOfferSelected &&
        (tirSelected ||
          termReduceSelected ||
          amountIncreaseSelected ||
          installmentReduceSelected) && (
          <SummaryWrapper id="summary-wrapper">
            <SummarySection>
              <SubtitleTextbold $fontSize={16}>
                Resumen de la opción seleccionada
              </SubtitleTextbold>
              <SummaryBox id="summary-box">
                <SummaryRow id="summary-row-1">
                  <SummaryElement id="summary-element-1">
                    <TodayHighlight id="today-highlight">
                      <TodayHighlightText id="today-highlight-text">
                        {counterOfferSelected &&
                        counterOfferSelected.financial.first_due &&
                        formatNumberToDate(
                          counterOfferSelected.financial.first_due,
                        ) === formatDate()
                          ? 'Hoy'
                          : formatNumberToDate(
                              counterOfferSelected.financial.first_due,
                            )}
                      </TodayHighlightText>
                    </TodayHighlight>
                    <TextBold id="summary-text-bold" $fontSize={14}>
                      Primer pago
                    </TextBold>
                  </SummaryElement>
                  <SummaryElement id="summary-element-2">
                    <TextBold id="summary-text-bold" $fontSize={20}>
                      {counterOfferSelected.financial?.installments.length >
                        0 &&
                        pricesHelper(
                          Number(
                            counterOfferSelected.financial.installments[0].fee,
                          ),
                        )}{' '}
                      €
                    </TextBold>
                  </SummaryElement>
                </SummaryRow>
                <SeparationLine id="separation-line" />
                <SummaryRow id="summary-row-2">
                  <SummaryElement id="summary-element-1">
                    <TextBold id="summary-text-bold" $fontSize={12}>
                      Cuotas restantes ({counterOfferSelected.term - 1} meses)
                    </TextBold>
                  </SummaryElement>
                  <SummaryElement id="summary-element-2">
                    <TextBold id="summary-text-bold" $fontSize={12}>
                      {counterOfferSelected.financial?.installments.length >
                        0 &&
                      counterOfferSelected.financial?.installments[1]
                        .due_number === 2
                        ? pricesHelper(
                            Number(
                              counterOfferSelected.financial?.installments[1]
                                .fee,
                            ),
                          )
                        : ''}{' '}
                      € /mes
                    </TextBold>
                  </SummaryElement>
                </SummaryRow>
              </SummaryBox>
            </SummarySection>
            <SummaryDetails id="summary-details">
              {counterOfferSelected.opening_fee_type !== '' &&
                openingFeeAmount > 0 && (
                  <SummaryDetailsInfo id="summary-details-info">
                    Com. apertura {pricesHelper(openingFeeAmount)} €
                    {counterOfferSelected.opening_fee_type === 'F'
                      ? ' financiada'
                      : ' a abonar en primera cuota'}
                    {counterOfferSelected.opening_fee_type === 'F' &&
                      ` (${pricesHelper(
                        openingFeeAmount / counterOfferSelected.term,
                      )} € por recibo)`}
                  </SummaryDetailsInfo>
                )}
            </SummaryDetails>
          </SummaryWrapper>
        )}
      <CounterOfferButtonSection id="main-button-section">
        <CounterOfferButton
          disabled={
            !tirSelected &&
            !termReduceSelected &&
            !amountIncreaseSelected &&
            !installmentReduceSelected
          }
          onClick={() => handleOptionClick()}
          $greenOption
        >
          Pagar ahora o guardar
        </CounterOfferButton>
        <CounterOfferButton onClick={() => handleNoContinue()} $secondaryOption>
          Salir sin guardar
        </CounterOfferButton>
      </CounterOfferButtonSection>
    </Container>
  );
};
